import React from 'react';
import {
  TableBody,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Typography,
  IconButton,
  Collapse,
  Tooltip,
} from '@material-ui/core';
import UpIcon from '@mui/icons-material/ArrowDropUp';
import DownIcon from '@mui/icons-material/ArrowDropDown';
import InfoIcon from '@mui/icons-material/InfoOutlined';

import { ProviderType } from 'generated/graphql';
import { TableBox } from 'retired/Box';
import { useCurrency } from 'retired/CurrencyFormat';
import { CurrencyCode } from 'utils/currencyFormat';
import { yellow, yellowTint, red, redTint } from 'utils/color-styles';

import { TransactionRow } from './TransactionRow';
import StatsSection from './StatsSection';
import Counter from './Counter';
import { TransactionsAggregate } from './summarizeTransactions';

export const PendingTransactionsTable = ({
  pendingTransactionsAggregate,
  accountCurrency,
  accountProvider,
}: {
  pendingTransactionsAggregate: TransactionsAggregate;
  accountCurrency: CurrencyCode;
  accountProvider: ProviderType;
}): JSX.Element => {
  const [open, setOpen] = React.useState(true);
  const { rates } = useCurrency(accountCurrency);

  const pendingTransactions = pendingTransactionsAggregate.transactions.map((transaction) => {
    if (transaction.totalAccountCurrency) {
      return transaction;
    }

    if (transaction.__typename === 'GivingAccountWithdrawal') {
      if (transaction.currencyCode === accountCurrency) {
        return {
          ...transaction,
          totalAccountCurrency: transaction.total,
          feeAmountAccountCurrency: transaction.feeAmount,
        };
      }

      return {
        ...transaction,
        totalAccountCurrency: transaction.total * rates?.[transaction.currencyCode],
        feeAmountAccountCurrency: transaction.feeAmount * rates?.[transaction.currencyCode],
      };
    }

    return transaction;
  });

  const openingBalance = pendingTransactions[0]?.balance || 0;
  const pendingPaid = pendingTransactions.reduce(
    (pendingTotal, pendingTransaction) => pendingTotal + pendingTransaction.totalAccountCurrency,
    0
  );
  const closingBalance = openingBalance - pendingPaid;
  const buttonToggledBorderColor = closingBalance > 0 ? yellow : red;
  const slideBackgroundColor = closingBalance > 0 ? yellowTint : redTint;

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          marginTop: 24,
          marginBottom: 12,
        }}
      >
        <Typography
          component="h3"
          onClick={(): void => setOpen(!open)}
          style={{
            fontSize: 18,
            fontWeight: 'bold',
            flexGrow: 1,
            marginLeft: 16,
          }}
        >
          <IconButton aria-label="expand row" size="small">
            {open ? <UpIcon /> : <DownIcon />}
          </IconButton>
          Pending
        </Typography>
        <StatsSection
          buttonToggledBorderColor={buttonToggledBorderColor}
          slideBackgroundColor={slideBackgroundColor}
          openCta="Pending totals"
        >
          <Counter
            isFirst
            label="Opening balance"
            amount={openingBalance}
            accountCurrency={accountCurrency}
          />
          <Counter
            label="Pending balance"
            amount={closingBalance}
            accountCurrency={accountCurrency}
          />
          <Counter
            label="Pending receipt"
            amount={pendingTransactionsAggregate.receivedAmount}
            accountCurrency={accountCurrency}
          />
          <Counter label="Pending payment" amount={pendingPaid} accountCurrency={accountCurrency} />
        </StatsSection>
      </div>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <TableBox>
          <Table
            style={{
              background: slideBackgroundColor,
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  size="medium"
                  style={{
                    width: 43,
                    maxWidth: '5%',
                  }}
                />
                <TableCell
                  size="medium"
                  align="left"
                  style={{
                    paddingLeft: 0,
                    width: '10%',
                  }}
                >
                  Date
                </TableCell>
                <TableCell
                  size="medium"
                  style={{
                    width: '10%',
                  }}
                >
                  Transaction
                </TableCell>
                <TableCell
                  size="medium"
                  style={{
                    width: '25%',
                  }}
                >
                  Description
                </TableCell>
                <TableCell
                  size="medium"
                  style={{
                    width: '10%',
                  }}
                >
                  Processor
                </TableCell>
                <TableCell
                  size="medium"
                  style={{
                    width: '12.5%',
                  }}
                >
                  Total Amount
                  <Tooltip title="Please note: The final amount may vary slightly due to currency conversions and potential disbursement constraints">
                    <InfoIcon style={{ marginTop: -2, marginLeft: 5 }} />
                  </Tooltip>
                </TableCell>
                <TableCell
                  size="medium"
                  style={{
                    width: '12.5%',
                  }}
                >
                  Balance
                </TableCell>
                <TableCell
                  size="medium"
                  style={{
                    width: '8%',
                  }}
                >
                  Files
                  <Tooltip title="Please note that upon potential disbursement constraints, the files might not be 100% disbursed">
                    <InfoIcon style={{ marginTop: -2, marginLeft: 5 }} />
                  </Tooltip>
                </TableCell>
                <TableCell
                  size="medium"
                  style={{
                    width: '15%',
                  }}
                >
                  Status
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <>
                {pendingTransactions.filter(Boolean).map((transaction) => (
                  <TransactionRow
                    key={transaction.id}
                    transaction={transaction}
                    accountCurrency={accountCurrency}
                    accountProvider={accountProvider}
                  />
                ))}
              </>
            </TableBody>
          </Table>
        </TableBox>
      </Collapse>
    </div>
  );
};
export default PendingTransactionsTable;
